import { CodableConceptCode } from "@intocare/datatypes/lib/concept";
import { codableConceptCodeToDisplayWith, codeToDisplayFromValueSetBundle } from "@intocare/datatypes/lib/display";
import { SupportedLocale } from "@intocare/datatypes/lib/i18n";
import { findConceptByCode, ValueSet } from "@intocare/datatypes/lib/value-set";
import { ISOLATION_MEASURE_TYPE_DISPLAY } from "@intocare/terminology/lib/value-set/display/isolation-measure-type.display";

export interface EvaluationSummary {
	id: string;
	status: EvaluationStatus;
	subject?: string;
	evaluationDate?: string;
	decisions?: string;
}

export interface EvaluationDetail {
	id: string;
	version: number;
	status: EvaluationStatus;
	subject?: string;
	evaluationDate?: string;
	createdOn: string;
	createdBy: { label: string };
	notes?: string;
	decisions?: string;
	finalizedBy?: { label: string };
	finalizedOn?: string;
	conclusion?: EvaluationConclusion;
	measures?: EvaluationMeasureDetail[];
}

/**
 * The concept represented by the specific evaluation measure, which is spread over multiple fields.
 *
 * - **ISOLATION**: {@link ISOLATION_MEASURE_TYPE_DISPLAY} Value-set code stored in `subtype.code`.
 * - **RESTRICTION**: CareTree code stored in `careTreeCode` (soft-link), display fallback in `subtype.text`.
 * - **ALTERNATIVE**: Free text, stored in `subtype.text`.
 */
export interface EvaluationMeasureConcept {
	type: EvaluationMeasureType;
	subtype: CodableConceptCode;
	careTreeCode?: string;
}

export const evaluationMeasureConceptToDisplayWith = (context: {
	locale: SupportedLocale;
	/** Will be `undefined` while it's loading, which is OK, because we fall back to the original display. */
	careTreeValueSet: ValueSet | undefined;
}) => {
	const alternativeMeasureConceptToDisplay = codableConceptCodeToDisplayWith((code) => code);
	const isolationMeasureConceptToDisplay = codableConceptCodeToDisplayWith(
		codeToDisplayFromValueSetBundle(ISOLATION_MEASURE_TYPE_DISPLAY),
	);

	const restrictionMeasureConceptToDisplay = (measure: EvaluationMeasureConcept) =>
		(measure.careTreeCode
			&& context.careTreeValueSet
			&& findConceptByCode(context.careTreeValueSet, measure.careTreeCode)?.coding.display)
		?? measure.subtype?.text
		?? measure.careTreeCode
		?? "-";

	return (evaluationMeasure: EvaluationMeasureConcept) => {
		switch (evaluationMeasure.type) {
			case "ALTERNATIVE":
				return alternativeMeasureConceptToDisplay(evaluationMeasure.subtype, context.locale);
			case "ISOLATION":
				return isolationMeasureConceptToDisplay(evaluationMeasure.subtype, context.locale);
			case "RESTRICTION":
				return restrictionMeasureConceptToDisplay(evaluationMeasure);
		}
	};
};

export interface EvaluationMeasureDetail<
	T extends EvaluationMeasureAction = EvaluationMeasureAction,
> extends EvaluationMeasureConcept {
	measureId: string;
	action: T;
	version: number;
	details?: string;
	startDate?: string;
	stopDate?: string;
	reason?: string;
}

interface BaseNewEvaluationMeasureInput extends EvaluationMeasureConcept {
	action: "NEW";
	details?: string;
	startDate?: string;
	stopDate?: string;
}

interface NewRestrictionMeasureInput extends BaseNewEvaluationMeasureInput {
	type: "RESTRICTION";
	subtype: CodableConceptCode;
	careTreeCode: string;
	reason: string;
}

interface NewIsolationMeasureInput extends BaseNewEvaluationMeasureInput {
	type: "ISOLATION";
	subtype: CodableConceptCode;
}

interface NewAlternativeMeasureInput extends BaseNewEvaluationMeasureInput {
	type: "ALTERNATIVE";
	subtype: CodableConceptCode;
}

type NewEvaluationMeasureInput = NewRestrictionMeasureInput | NewIsolationMeasureInput | NewAlternativeMeasureInput;

interface ActiveEvaluationMeasureInput {
	action: "ACTIVE";
	measureId: string;
	version: number;
}

interface StoppedRestrictionMeasureInput {
	action: "STOPPED";
	type: "RESTRICTION";
	measureId: string;
	version: number;
	reason: string;
}

interface StoppedNonRestrictionMeasureInput {
	action: "STOPPED";
	type: "ISOLATION" | "ALTERNATIVE";
	measureId: string;
	version: number;
}

type StoppedEvaluationMeasureInput = StoppedRestrictionMeasureInput | StoppedNonRestrictionMeasureInput;

export type EvaluationMeasureInput =
	NewEvaluationMeasureInput | ActiveEvaluationMeasureInput | StoppedEvaluationMeasureInput;

export type EvaluationStatus = "DRAFT" | "FINALIZED";

export type EvaluationConclusion = "FUP_ONGOING" | "FUP_RESOLVED";

export type EvaluationMeasureType = "ISOLATION" | "RESTRICTION" | "ALTERNATIVE";

export type EvaluationMeasureAction = "NEW" | "ACTIVE" | "STOPPED";

export type ActiveMeasureTypes = Extract<EvaluationMeasureType, "RESTRICTION" | "ISOLATION">;
